import { useCallback, useEffect, useState } from 'react';
import { useBoard, useBoardMeta } from './context';
import { PanelFrame } from './floating';
import { formatBytes } from './items/FileItem';
import { sweepUnusedAssets, type SweepResult } from './storage';
import { setToolSettings, useToolSettings } from './toolSettings';

const QUALITIES = [0.8, 0.9, 0.95];

/** Image import compression (per device) and this board's server storage, with a clean-up for unused files. */
export function StoragePanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const meta = useBoardMeta();
  const s = useToolSettings((t) => t);
  const canSweep = board.remote && (meta.role === 'owner' || meta.role === 'editor');
  const [info, setInfo] = useState<SweepResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      setInfo(await sweepUnusedAssets(board, false));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [board]);

  useEffect(() => {
    if (canSweep) void check();
  }, [canSweep, check]);

  const clean = async () => {
    if (!info || !info.unused.count) return;
    const what = `${info.unused.count} unused ${info.unused.count === 1 ? 'file' : 'files'} (${formatBytes(info.unused.bytes)})`;
    if (!confirm(`Remove ${what} from the server?\n\nThis clears the board's undo history: deleted images cannot be brought back afterwards.`)) return;
    setBusy(true);
    setDone(null);
    try {
      const r = await sweepUnusedAssets(board, true);
      setDone(`Removed ${r.removed.length} ${r.removed.length === 1 ? 'file' : 'files'}.`);
      setInfo(await sweepUnusedAssets(board, false));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelFrame kind="storage" title="Storage" className="storage-panel" onClose={onClose}>
      <div className="insp-body">
        <div className="insp-label">Image imports (this device)</div>
        <label className="opt-check">
          <input type="checkbox" checked={s.importWebp} onChange={(e) => setToolSettings({ importWebp: e.target.checked })} />
          Store originals as lossy WebP
        </label>
        {s.importWebp && (
          <>
            <div className="insp-label">Quality</div>
            <div className="btn-row">
              {QUALITIES.map((q) => (
                <button key={q} type="button" className={`btn${s.importQuality === q ? ' is-active' : ''}`} onClick={() => setToolSettings({ importQuality: q })}>
                  {Math.round(q * 100)}
                </button>
              ))}
            </div>
          </>
        )}
        <div className="insp-muted">
          Applies to images imported from now on; dimensions are never changed here (Board settings can cap the longest side per board). PNG, JPEG, BMP
          and AVIF are re-encoded; animated GIFs and attachments are kept as they are, and so is any image that would not get smaller. The same file
          imported twice onto one board is stored once.
        </div>

        {canSweep && (
          <>
            <div className="insp-label">This board on the server</div>
            {info ? (
              <div>
                {info.total.count} {info.total.count === 1 ? 'file' : 'files'} · {formatBytes(info.total.bytes)}
                <br />
                {info.unused.count ? (
                  <span>
                    {info.unused.count} unused · {formatBytes(info.unused.bytes)}
                  </span>
                ) : (
                  <span className="insp-muted">nothing unused</span>
                )}
              </div>
            ) : (
              <div className="insp-muted">{error ? 'Could not check.' : 'Checking…'}</div>
            )}
            <div className="btn-row">
              <button type="button" className="btn" disabled={busy || !info?.unused.count} onClick={() => void clean()}>
                Remove unused files
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => void check()}>
                Recheck
              </button>
            </div>
            {done && <div className="insp-muted">{done}</div>}
            {error && <div className="form-error">{error}</div>}
            <div className="insp-muted">Unused: uploaded more than an hour ago and on no item any more. Removing them clears this board's undo history.</div>
          </>
        )}
      </div>
    </PanelFrame>
  );
}
